import Icon from '@components/v2/ui/Icon/Icon';
import { Box, Checkbox } from '@mui/material';
import classNames from 'classnames';
import { CircleAlert } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import Typography from '../../../../../../../../../../../components/Typography/Typography';
import { PackQuantitiesWithMultiplier } from '../../../../../../../../../../../services/modules/product-colors-packs/product-colors-packs.types';
import { breakWordsByLetter } from '../../../../../../../../../contexts/order.utils';
import HorizontalScroll from '../../../../../components/HorizontalScroll/HorizontalScroll';
import { usePackStyles } from '../../../../styles/usePackStyles';
import usePackMenuItem from '../../hooks/usePackMenuItem';
import { getPackNameColor } from '../../utils/orderProductPacksMenuItem.utils';
import PacksMenuItemGridMultiplyDesktop from './PacksMenuItemGridMultiplyDesktop';

interface PacksMenuItemPropsDesktop {
  pack: PackQuantitiesWithMultiplier;
  itemWidth: number;
  isPackSelected: boolean;
  isPackWithError: boolean;
  isPackWithUnavailableStock: boolean;
  onClickRow: (quantity: number) => void;
  onClickGridMultiply: (multiplier: number) => void;
}

const PacksMenuItemDesktop = ({
  pack,
  itemWidth,
  onClickRow,
  isPackSelected,
  isPackWithError,
  onClickGridMultiply,
  isPackWithUnavailableStock,
}: PacksMenuItemPropsDesktop) => {
  const { classes: packStyles } = usePackStyles();
  const gridMultiply = useRef<HTMLInputElement>();
  const [hasClickedMultiplier, setHasClickedMultiplier] = useState(false);
  const { showStock, handleInput, stockAvailableAmount, packItemInReadMode } = usePackMenuItem({
    pack,
  });

  useEffect(() => {
    if (
      gridMultiply.current &&
      pack.multiplier !== gridMultiply.current.valueAsNumber &&
      !hasClickedMultiplier
    ) {
      gridMultiply.current.value = pack.multiplier?.toString() || '';
      setHasClickedMultiplier(false);
    }
    if (gridMultiply.current && !isPackSelected) {
      gridMultiply.current.value = '';
    }
  }, [isPackSelected, pack, hasClickedMultiplier]);

  const handleClickRow = () => {
    setHasClickedMultiplier(false);
    const multiplier = gridMultiply.current?.valueAsNumber || 1;
    onClickRow(multiplier);
  };

  const handleClickGridMultiply = () => {
    const multiplier = gridMultiply.current?.valueAsNumber ?? 1;
    if (!isPackSelected && multiplier === 0) {
      return;
    }
    if (multiplier > 1) {
      setHasClickedMultiplier(true);
    }
    onClickGridMultiply(multiplier);
  };

  return (
    <Box
      className={classNames(
        {
          [packStyles.itemRow]: true,
          [packStyles.cursorPointer]: !packItemInReadMode,
          [packStyles.selectedItemRow]: isPackSelected && !isPackWithError,
          [packStyles.errorItemRow]: isPackSelected && isPackWithError,
        },
        packStyles.menuItemDesktopContainer,
      )}
      onClick={handleClickRow}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        height: 54,
      }}
    >
      <Box
        className="stickyPackNameStylesPropagation"
        sx={{
          width: 160,
          marginRight: '16px',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            height: '38px',
          }}
        >
          {isPackWithError && isPackSelected ? (
            <Box className={packStyles.errorIcon} sx={{ display: 'flex', color: 'error.main' }}>
              <Icon icon={CircleAlert} size="lg" />
            </Box>
          ) : (
            <Checkbox
              tabIndex={-1}
              color="primary"
              checked={isPackSelected}
              disableRipple={packItemInReadMode}
              className={classNames({
                [packStyles.cursorUnset]: packItemInReadMode,
              })}
            />
          )}
          <Typography
            color={getPackNameColor({ isPackSelected, isPackWithError })}
            className={packStyles.gridName}
            title={pack.name}
          >
            {breakWordsByLetter(pack.name.toUpperCase())}
          </Typography>
        </Box>
      </Box>
      <HorizontalScroll>
        <div className={packStyles.itemContainer}>
          {pack.quantitiesBySize.map((item) => (
            <div
              key={`${pack.id}-${item.size.id}`}
              className={packStyles.sizeQuantityItemContainer}
              style={{ width: itemWidth }}
            >
              <Typography color="textSecondary" align="center" noWrap>
                {item.size.name}
              </Typography>
              <Typography color="textPrimary" align="center" noWrap>
                {item.quantity}
              </Typography>
            </div>
          ))}
        </div>
      </HorizontalScroll>
      <Box
        className="stickyPackMultiplierStylesPropagation"
        sx={{
          paddingRight: '9px',
        }}
      >
        <PacksMenuItemGridMultiplyDesktop
          ref={gridMultiply}
          showStock={showStock}
          handleInput={handleInput}
          readMode={packItemInReadMode}
          packMultiplier={pack.multiplier}
          willConsumeStock={pack.willConsumeStock}
          stockAvailableAmount={stockAvailableAmount}
          handleClickGridMultiply={handleClickGridMultiply}
          isPackWithUnavailableStock={isPackWithUnavailableStock}
        />
      </Box>
    </Box>
  );
};

export default PacksMenuItemDesktop;
